import type { ReactNode } from 'react';
import React from 'react';
import { withRouter } from 'next/router';
import type { Router } from 'next/router';
import { handleComponentError } from 'Errors/clientErrorHandler';
import { isStrictlyProductionEnv } from 'Utils/context/contextUtils';
type Props = {
  children: ReactNode;
  useNativeErrorHandling?: boolean;
};
type WithRouterProps = Props & {
  router: Router;
};
type State = {
  hasError: boolean;
};

class ErrorBoundaryComponent extends React.Component<WithRouterProps, State> {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
    };
  }

  static getDerivedStateFromError() {
    return {
      hasError: true,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  componentDidCatch(error: any, info: any) {
    handleComponentError(error, info);

    if (!isStrictlyProductionEnv()) {
      // In dev/staging: log prominently so the error is visible in browser
      // DevTools and server logs, instead of silently disappearing.
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary] Caught error (non-prod):', error, info);
    }
  }

  render() {
    const { router, children } = this.props;

    if (this.state.hasError) {
      if (isStrictlyProductionEnv()) {
        const { locale } = router.query;
        router.replace({
          pathname: '/500',
          query: Boolean(locale) && {
            locale,
          },
        });
        this.setState({
          hasError: false,
        });
      }

      return null;
    }

    return children;
  }
}

export const ErrorBoundary = withRouter<Props>(ErrorBoundaryComponent);
